import { useSeeHint } from "components/hint/Hint";
import { useGetState, useSetState } from "components/ReduxProvider";
import { useViewerQuery, useViewerXps } from "queries/viewerQuery";
import React from "react";
import { Trans } from "react-i18next";
import styled, { keyframes } from "styled-components/macro";
import { dispatchEvent, EVENT } from "tools/events";
import QuestReward from "./QuestReward";
import TutorialPointer from "../../../tutorial/TutorialPointer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentDots, faMessages } from "@duocards/pro-regular-svg-icons";
import { QuestMessage } from "./misc/QuestMessage";
import { Quest } from "./Quest";
import { PRACTICE_BUTTON_ID } from "components/other/BottomNavigation";
export const AI_ASSISTANT_QUEST = "aiAssistantQuest";

const Wrap = styled.div``;

const SDiv = styled.div<{ crossed?: boolean }>`
  text-decoration: ${({ crossed }) => (crossed ? "line-through" : "none")};
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
`;

const pulsingRotatedKF = keyframes`
  0%, 100% { transform: scale(1) rotate(95deg) translateY(-7px); }
  50% {  transform: scale(1.3) rotate(85deg) translateY(7px);}
`;

const STutorialPointer = styled(TutorialPointer)`
  position: absolute;
  left: -15%;
  animation: ${pulsingRotatedKF} 1.5s ease-in-out infinite;
`;

const SRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const REWARD_XP = 5;

type Props = {};

export const useIsQuestAiAssistant = () => {
  const { hintWasSeen } = useViewerQuery();
  const currentQuest = useGetState("currentQuest");
  const questAiAssistantCompleted = useGetState("questAiAssistant");
  const { xps } = useViewerXps();
  if (!currentQuest || !xps) return false;
  return (
    !hintWasSeen(AI_ASSISTANT_QUEST) &&
    currentQuest?.name === "AiAssistantQuest" &&
    xps?.total >= currentQuest.startXps &&
    !questAiAssistantCompleted
  );
};

const AiAssistantQuest: React.FC<Props> = () => {
  const { hintWasSeen } = useViewerQuery();
  const questAiAssistant = useGetState("questAiAssistant");
  const seeHint = useSeeHint();
  const { xps } = useViewerXps();
  const setLastQuestXps = useSetState("lastQuestXps");

  return (
    <Quest questName="AiAssistantQuest" requiredExps={1}>
      <QuestMessage visible={!hintWasSeen(AI_ASSISTANT_QUEST)}>
        <Wrap>
          <QuestReward
            rewardedXps={REWARD_XP}
            claim={questAiAssistant}
            onDone={() => {
              seeHint(AI_ASSISTANT_QUEST);
              setTimeout(() => dispatchEvent(EVENT.updateMain));
              setLastQuestXps((xps?.total || 0) + REWARD_XP);
            }}
          />
          <SDiv crossed={questAiAssistant}>
            <Trans parent="div">Try our AI-assistant</Trans>
            <SRow>
              <Trans>1. Go to the Practice section</Trans>
              <FontAwesomeIcon icon={faMessages} />
            </SRow>
            <SRow>
              <Trans>2. Tap "Chat"</Trans>
              <FontAwesomeIcon icon={faCommentDots} />
            </SRow>
            <Trans parent="div">3. Ask for something…</Trans>
          </SDiv>

          {!questAiAssistant && (
            <STutorialPointer pulsing container={() => document.getElementById(PRACTICE_BUTTON_ID)} />
          )}
        </Wrap>
      </QuestMessage>
    </Quest>
  );
};

export default AiAssistantQuest;
